import React from "react";
import { Check } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";
import { formatPriceWithLocale } from "../../utils/pricing";
import { PlanCTA } from "./PricingCard";

// The top plan's card on /pricing. Same props and checkout as PricingCard
// (the button is the shared PlanCTA); only the look differs, so it reads as
// the studio tier instead of a third copy of the other two.
const L = {
  card: "tw-bg-[linear-gradient(160deg,#F5F5F7_0%,#C9CCD3_45%,#8E929C_100%)] tw-text-[#111318]",
  cta: "tw-bg-[#111318] tw-text-white hover:tw-bg-black",
  tick: "tw-text-[#111318]",
  muted: "tw-text-[#111318]/70",
  badge: "tw-bg-[#111318] tw-text-white",
};

const StudioCard = ({
  plan,
  label = "Studio",
  status,
  currency,
  price,
  originalPrice,
  benefits,
  everythingIn = null,
  setClientSecret,
  onSelect,
  badge = null,
  subtitle = "",
}) => {
  const t = useT();
  const fmt = (n) => formatPriceWithLocale(n, currency);

  const priceNote =
    originalPrice != null
      ? t("pricing.card.firstMonthThen", { price: fmt(originalPrice) })
      : t("pricing.card.perMonth");
  const ctaLabel =
    originalPrice != null
      ? t("pricing.card.startFor", { price: fmt(price) })
      : t("pricing.cta.get", { plan: label });

  return (
    <div
      className={`tw-relative tw-flex tw-h-full tw-flex-col tw-gap-4 tw-rounded-[32px] tw-p-[22px] md:tw-rounded-[36px] md:tw-p-7 ${L.card}`}
    >
      {badge && (
        <span
          className={`tw-absolute tw--top-3.5 tw-left-[30px] tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-extrabold ${L.badge}`}
        >
          {badge}
        </span>
      )}

      <div>
        <div className="jam-display tw-text-[24px] tw-font-extrabold md:tw-text-[28px]">
          {label}
        </div>
        <div className={`tw-text-sm tw-font-semibold ${L.muted}`}>
          {subtitle}
        </div>
      </div>

      <div>
        <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-2">
          <span className="jam-display tw-text-[40px] tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] md:tw-text-[52px]">
            {fmt(price)}
          </span>
          {originalPrice != null && (
            <span className="tw-text-base tw-font-bold tw-line-through tw-opacity-60">
              {fmt(originalPrice)}
            </span>
          )}
        </div>
        <div className={`tw-mt-1 tw-text-[13px] tw-font-bold ${L.muted}`}>
          {priceNote}
        </div>
      </div>

      <PlanCTA
        plan={plan}
        status={status}
        currency={currency}
        setClientSecret={setClientSecret}
        onSelect={onSelect}
        label={ctaLabel}
        className={L.cta}
      />

      <div className="tw-flex tw-flex-col tw-gap-2.5 tw-text-sm tw-font-bold">
        {everythingIn && (
          <span className={L.muted}>
            {t("pricing.card.everythingIn", { plan: everythingIn })}
          </span>
        )}
        {benefits.map((benefit) => (
          <span key={benefit} className="tw-flex tw-items-start tw-gap-2">
            <Check
              size={16}
              strokeWidth={3}
              className={`tw-mt-0.5 tw-flex-shrink-0 ${L.tick}`}
            />
            {benefit}
          </span>
        ))}
      </div>
    </div>
  );
};

export default StudioCard;
